import configs from '@/config'
// Sentry
import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { browserTracingIntegration } from '@sentry/vue'
import { Replay } from '@sentry/replay'
import { logger, getDeviceId } from '@ahaslides-product/frontend-toolkit'

const ignoreErrors = [
  'zaloJSV2 is not defined',
  'Can\'t find variable: zaloJSV2',
  'ResizeObserver loop limit exceeded',
  'Cannot read property \'getReadModeConfig\' of undefined',
  'Cannot read property \'getReadModeRender\' of undefined',
  'Cannot read property \'getReadModeExtract\' of undefined',
  'UnhandledRejection: Non-Error promise rejection captured with value: Object Not Found Matching Id:2',
]

const replay = new Replay({ maskAllText: false })

const isResponseShaped = (error) => (
  !!error
  && typeof error === 'object'
  && !(error instanceof Error)
  && Object.prototype.hasOwnProperty.call(error, 'status')
  && ('ok' in error || 'headers' in error)
)

const truncate = (text, max = 500) => (
  typeof text === 'string' && text.length > max ? `${text.slice(0, max)}...` : text
)

// Sentry can't serialise a raw Response-shaped object, so every catch block that
// captures one collapses into a single unactionable `<object>.value` cluster.
// Relabel it into a groupable HTTP-status signal instead of dropping it.
export const normaliseResponseException = (event, hint) => {
  try {
    const error = hint?.originalException

    if (!isResponseShaped(error)) return event

    const { status, ok, bodyText } = error
    const url = error.url ?? error.config?.url ?? error.request?.responseURL

    const message = `HTTP ${status}${url ? ` ${url}` : ''}`

    if (event.exception?.values?.[0]) {
      event.exception.values[0].type = 'HttpResponseError'
      event.exception.values[0].value = message
    } else {
      event.message = message
    }

    event.fingerprint = ['http-response', String(status)]
    event.tags = { ...event.tags, http_status: status }
    event.extra = {
      ...event.extra,
      status,
      ok,
      url,
      bodyText: truncate(bodyText),
    }

    return event
  } catch {
    return event
  }
}

const TRANSIENT_RESPONSE_STATUSES = new Set([401, 403, 404, 408, 409, 429])

const isVueResourceResponse = (error) => (
  error != null &&
  typeof error === 'object' &&
  !(error instanceof Error) &&
  typeof error.status === 'number' &&
  ('ok' in error || 'bodyText' in error || 'headers' in error || 'body' in error)
)

// vue-resource rejects its promise with the raw HTTP Response object (not an
// Error) on any non-2xx/failed request; the ~159 `catch { captureException }`
// sites then hand Sentry that object, which all collapses into one useless
// `<object>.value` issue. Classify at this single choke point: drop expected
// transients, relabel genuine faults into one readable, correctly-grouped issue.
export const classifyEvent = (event, hint) => {
  const error = hint?.originalException

  if (error instanceof Error) return event

  // network cut-off — Response carries status 0 (preserved from PR #594)
  if (error?.status === 0) return null

  if (isVueResourceResponse(error)) {
    const { status } = error
    if (TRANSIENT_RESPONSE_STATUSES.has(status)) return null

    const url = typeof error.url === 'string' ? error.url : undefined
    const endpoint = url ? url.split('?')[0] : undefined
    const bodyText = typeof error.bodyText === 'string' ? error.bodyText.slice(0, 500) : undefined

    event.exception = {
      ...event.exception,
      values: [{
        ...event.exception?.values?.[0],
        type: 'HttpResponseError',
        value: `HTTP ${status}${endpoint ? ` ${endpoint}` : ''}`,
      }],
    }
    event.fingerprint = ['http-response-error', String(status), endpoint || 'unknown']
    event.extra = {
      ...event.extra,
      httpResponse: { status, url: endpoint, bodyText },
    }
    return event
  }

  return event
}

// iOS Safari can reject AudioContext.resume() with this exact type+message
// when the audio hardware is busy/interrupted — an expected, non-actionable
// condition. Matching on both fields keeps this from masking anything else.
export const shouldDropEvent = (hint) => {
  const error = hint?.originalException
  const name = error?.name
  const message = error?.message || ''
  return name === 'InvalidStateError' && message.includes('Failed to start the audio device')
}

const init = () => {
  if (!configs.isSentryEnabled) return

  Sentry.init({
    release: configs.sentryRelease,
    dsn: configs.sentry,
    environment: configs.env,
    integrations: [
      browserTracingIntegration(),
      replay,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0,
    Vue,
    ignoreErrors,
    denyUrls: [
      /analytics\.tiktok\.com/i,
    ],
    tracingOptions: {
      trackComponents: true,
    },
    enableLogs: true,
    beforeSend(event, hint) {
      const { originalException: error } = hint

      if (configs.env !== 'production') {
        // log error to browser console
        // for better visibility into exceptions
        if (event?.extra?.error) {
          // eslint-disable-next-line no-console
          logger(event.extra.error)
        } else if (error) {
          // eslint-disable-next-line no-console
          logger(error)
        }
      }
      // ignore sentry error due to network cut-off
      if (error?.status === 0) return null
      if (shouldDropEvent(hint)) return null
      return classifyEvent(event, hint)
    },
  })
}

const captureException = (error, options) => {
  if (!configs.isSentryEnabled) return

  try {
    // Preserve a real Error's stack; only synthesize one when handed a string/other.
    Sentry.captureException(error instanceof Error ? error : new Error(error), options)
  } catch {
    // do nothing
  }
}

const addBreadcrumb = (breadcrumb = {}) => {
  if (!configs.isSentryEnabled) return

  try {
    const editedBreadcrumb = { ...breadcrumb }

    if (breadcrumb.level === 'info') {
      editedBreadcrumb.level = 'info'
    }

    Sentry.addBreadcrumb(editedBreadcrumb)
  } catch {
    // do nothing
  }
}

const startSessionReplay = async () => {
  if (!configs.isSentryEnabled) return

  try {
    await replay.flush()
  } catch (error) {
    captureException(error)
  }
}

const stopSessionReplay = async () => {
  if (!configs.isSentryEnabled) return

  try {
    await replay.stop()
  } catch (error) {
    captureException(error)
  }
}

const getTabId = () => {
  let tabId = sessionStorage.getItem('aha-tab-id')
  if (!tabId) {
    tabId = new Date().getTime()
    sessionStorage.setItem('aha-tab-id', tabId)
  }
  return tabId
}

const augmentData = (data) => {
  if (!data) return data
  return {
    ...data,
    deviceId: getDeviceId(),
    tabId: getTabId(),
  }
}

const setTags = (attributes = {}) => {
  if (!configs.isSentryEnabled) return

  try {
    // Set as tags for filtering and searching
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        Sentry.setTag(key, value)
      }
    })
  } catch (error) {
    // do nothing
    logger('error setting sentry tags', error)
  }
}

export const logInfo = (message, data) => {
  if (!configs.isSentryEnabled) return
  Sentry.logger.info(message, augmentData(data))
  // also log to file
  logger(message, data)
}

export const logWarn = (message, data) => {
  if (!configs.isSentryEnabled) return
  Sentry.logger.warn(message, augmentData(data))
  // also log to file
  logger(message, data)
}

export default {
  init,
  captureException,
  addBreadcrumb,
  startSessionReplay,
  stopSessionReplay,
  info: logInfo,
  warn: logWarn,
  setTags,
}
